import { useState } from 'react';
import {
  ClientSession,
  ClientSessionGroup,
  ClientSessionIndividual,
  ClientSessionParticipant,
} from '../../../../../generated/graphql';
import SessionActions from './SessionActions';
import IndividualSessionRow from './IndividualSessionRow';
import GroupSessionRow from './GroupSessionRow';
import classNames from 'classnames';
import DismissModal from '../modals/DismissModal';
import RestoreModal from '../modals/RestoreModal';
import { useTableStore } from '../../hooks/useTableStore';
import { useSessionsData } from '../../hooks/useSessionData';
import Pagination from '../Pagination';
import { useSessionsStore } from '../../hooks/useSessionStore';
import { PAGE_SIZE } from '../../utils/constants';
import Checkbox from '../../../../components/Checkbox';
import TooltipLight from '../../../../components/TooltipLight';
import { QuestionMarkCircleIcon } from '@heroicons/react/outline';

export type Selectable = ClientSession | ClientSessionParticipant;

const isGroup = (item: ClientSession): item is ClientSessionGroup => {
  return item.__typename === 'ClientSessionGroup';
};

const isIndividual = (item: ClientSession): item is ClientSessionIndividual => {
  return item.__typename === 'ClientSessionIndividual';
};

const ClientSessionsTable: React.FC = () => {
  const {
    sessions,
    pageInfo,
    totalCount,
    refetch: refetchSessions,
  } = useSessionsData();
  const {
    selectedSessions,
    expandedGroups,
    toggleSelection,
    toggleGroupSelection,
    toggleGroup,
    clearSelection,
    selectAllSessions,
  } = useTableStore();
  const [showDismissModal, setShowDismissModal] = useState(false);
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [sessionsToDelete, setSessionsToDelete] = useState<ClientSession[]>([]);
  const [sessionsToRestore, setSessionsToRestore] = useState<ClientSession[]>(
    [],
  );
  const { setNextPage, setPreviousPage, currentPage } = useSessionsStore();

  const handleDismiss = (sessions: ClientSession[]) => {
    setSessionsToDelete(sessions);
    setShowDismissModal(true);
  };

  const handleRestore = (sessions: ClientSession[]) => {
    setSessionsToRestore(sessions);
    setShowRestoreModal(true);
  };

  const handleDismissSuccess = async () => {
    clearSelection();
    await refetchSessions();
  };

  const handleRestoreSuccess = async () => {
    clearSelection();
    await refetchSessions();
  };

  const handleNextPage = () => {
    setNextPage();
  };

  const handlePreviousPage = () => {
    setPreviousPage();
  };

  const areAllSessionsSelected = sessions.every((session) =>
    selectedSessions.some((s) => s.id === session.id),
  );

  return (
    <div className="mx-auto w-full">
      {showDismissModal && (
        <DismissModal
          sessions={sessionsToDelete}
          onClose={() => setShowDismissModal(false)}
          onSuccess={handleDismissSuccess}
        />
      )}
      {showRestoreModal && (
        <RestoreModal
          sessions={sessionsToRestore}
          onClose={() => setShowRestoreModal(false)}
          onSuccess={handleRestoreSuccess}
        />
      )}

      <div className="sticky top-0 z-10 bg-white">
        <SessionActions
          selectedSessions={selectedSessions}
          clearSelection={clearSelection}
          sessions={sessions as ClientSession[]}
          onDismiss={handleDismiss}
          onRestore={handleRestore}
        />

        {/* Header */}
        <div className="grid min-w-full grid-cols-[auto,auto,1fr,1fr,1fr,1fr,auto] gap-4 py-2 px-4 text-left text-small-caption font-medium text-neutral-150">
          <div className="flex w-6 items-center">
            <Checkbox
              id="select-all"
              checked={areAllSessionsSelected}
              onChange={() => selectAllSessions(sessions)}
            />
          </div>
          <div className="flex w-9 items-center justify-center">
            <TooltipLight content="Session source" placement="top">
              <span
                role="img"
                aria-label="Session source"
                className="inline-flex items-center justify-center"
              >
                <QuestionMarkCircleIcon
                  className="h-5 w-5 text-neutral-125"
                  aria-hidden
                />
              </span>
            </TooltipLight>
          </div>
          <div className="flex min-w-[150px] items-center">Client</div>
          <div className="flex min-w-[100px] max-w-[220px] items-center">
            Practitioner(s)
          </div>
          <div className="flex min-w-[110px] items-center">Title</div>
          <div className="flex min-w-[110px] items-center">Date & Time</div>
          <div className="flex w-24 items-center">Actions</div>
        </div>
      </div>

      <div className="flex w-full flex-col">
        <div className={classNames('w-full divide-y divide-neutral-50')}>
          {sessions.map((session: ClientSession) => {
            if (isIndividual(session)) {
              return (
                <IndividualSessionRow
                  key={session.id}
                  session={session}
                  selectedSessions={selectedSessions}
                  onToggleSelection={toggleSelection}
                  onDismiss={(session) => handleDismiss([session])}
                />
              );
            }

            if (isGroup(session)) {
              return (
                <GroupSessionRow
                  key={session.id}
                  group={session}
                  selectedSessions={selectedSessions}
                  expandedGroups={expandedGroups}
                  onToggleGroup={toggleGroup}
                  onToggleSelection={toggleSelection}
                  onToggleGroupSelection={toggleGroupSelection}
                />
              );
            }

            return null;
          })}
        </div>
        <div className="sticky bottom-0 z-10 mt-2 flex justify-center border-t border-neutral-50 bg-white">
          <Pagination
            hasNextPage={pageInfo?.hasNextPage ?? false}
            hasPreviousPage={pageInfo?.hasPreviousPage ?? false}
            totalCount={totalCount}
            currentPage={currentPage}
            pageSize={PAGE_SIZE}
            itemCountOnPage={sessions.length}
            onNextPage={handleNextPage}
            onPreviousPage={handlePreviousPage}
          />
        </div>
      </div>
    </div>
  );
};

export default ClientSessionsTable;
